/**
 * sessionStorage key registry for promotional UI components (Banner, InfoCard, Chip).
 *
 * Key format:
 *   Dismissed state:  jigsaw_promo_dismissed_<id>   → "1"
 *   Chip active:      jigsaw_promo_chip_active_<id>  → "1"
 *
 * Session lifetime: keys persist across page refreshes but clear when the browser
 * tab is closed (sessionStorage behaviour).
 *
 * ID naming convention:
 *   Banner:   banner_<page>           e.g. banner_gst_recon
 *   InfoCard: card_<feature>_<style>  e.g. card_supplier_dark
 *   Chip:     chip_<feature>          e.g. chip_supplier_intelligence
 */

export const PROMO_STORAGE_PREFIX = 'jigsaw_promo_dismissed_';
export const CHIP_ACTIVE_PREFIX   = 'jigsaw_promo_chip_active_';

// ─── Supplier Intelligence Promotion ─────────────────────────────────────────

export const SI_BANNER_KEYS = {
  GST_RECON:   'banner_si_gst_recon',
  DASHBOARD:   'banner_si_dashboard',
  REPORTS:     'banner_si_reports',
  FINANCE:     'banner_si_finance',
  CONNECTIONS: 'banner_si_connections',
} as const;

export const SI_CARD_KEYS = {
  DARK_MAIN:        'card_si_dark_main',
  LIGHT_REPORT_TIP: 'card_si_light_report_tip',
} as const;

export const SI_CHIP_KEYS = {
  MAIN: 'chip_si_main',
} as const;

export const SI_INFOCARD_PAGES = {
  WORKSPACESTORE: { cardId: SI_CARD_KEYS.DARK_MAIN, chipId: SI_CHIP_KEYS.MAIN, promoKey: 'si_infocard_workspace_store' },
  REPORTS:        { cardId: SI_CARD_KEYS.DARK_MAIN, chipId: SI_CHIP_KEYS.MAIN, promoKey: 'si_infocard_reports'         },
  PNL_STATEMENT:  { cardId: SI_CARD_KEYS.DARK_MAIN, chipId: SI_CHIP_KEYS.MAIN, promoKey: 'si_infocard_pnl'             },
} as const;

// ─── Helpers ──────────────────────────────────────────────────────────────────

export const getPromoStorageKey  = (id: string) => PROMO_STORAGE_PREFIX + id;
export const getChipActiveKey    = (chipId: string) => CHIP_ACTIVE_PREFIX + chipId;

export const isPromoDismissed    = (id: string) =>
  sessionStorage.getItem(getPromoStorageKey(id)) === '1';

export const isChipActive        = (chipId: string) =>
  sessionStorage.getItem(getChipActiveKey(chipId)) === '1';

export const setPromoDismissed   = (id: string) =>
  sessionStorage.setItem(getPromoStorageKey(id), '1');

export const dispatchPromoDismiss = (id: string) => {
  setPromoDismissed(id);
  window.dispatchEvent(new CustomEvent('jigsaw:promo:dismiss', { detail: { id } }));
};

export const activateChip        = (chipId: string) => {
  sessionStorage.setItem(getChipActiveKey(chipId), '1');
  window.dispatchEvent(new CustomEvent('jigsaw:promo:chip:activate', { detail: { chipId } }));
};

export const SI_SIDEBAR_TOOLTIP_KEY = 'si_sidebar_tooltip';

export const getSILightCardKey = (companyId: number | string, dpIdentifier: string) =>
  `card_si_light_${companyId}_${dpIdentifier}`;

// ─── AWS AppConfig keys for per-page SI promo enable/disable ─────────────────

export const SI_PROMO_CONFIG_KEYS = {
  BANNER_REPORT_LIST:       'si_banner_report_list',
  BANNER_PNL:               'si_banner_pnl',
  BANNER_REPORT_DETAILS:    'si_banner_report_details',
  BANNER_WORKSPACE_STORE:   'si_banner_workspace_store',
  BANNER_SALES_MARKETING:   'si_banner_sales_marketing',
  BANNER_DASHBOARD:         'si_banner_dashboard',
  INFOCARD_WORKSPACE_STORE: 'si_infocard_workspace_store',
  INFOCARD_REPORTS:         'si_infocard_reports',
  INFOCARD_PNL:             'si_infocard_pnl',
  INFOCARD_EXPAND_VIEW:     'si_infocard_expand_view',
  TOOLTIP_SIDEBAR:          'si_tooltip_sidebar',
} as const;

// Missing key or unloaded config → show (backward-compatible default)
export const isSIPromoEnabled = (config: Record<string, boolean>, key: string): boolean =>
  config[key] !== false;

export const resetInfoCard       = (cardId: string, chipId: string) => {
  sessionStorage.removeItem(getPromoStorageKey(cardId));
  sessionStorage.removeItem(getChipActiveKey(chipId));
  window.dispatchEvent(new CustomEvent('jigsaw:promo:infocard:reset', { detail: { cardId } }));
};
